<script lang="ts">
	import sendWebMessage from "@api/mutations/sendWebMessage.graphql";
	import type { HTMLFormAttributes } from "svelte/elements";
	import type { WebMessageInput } from "@core/schema/WebMessage.js";
	import { contactId } from "@core/schema/ids/contactId.js";
	import { inquiryId } from "@core/schema/ids/inquiryId.js";
	import { getPrivacyPolicyPath } from "@core/schema/paths/getPrivacyPolicyPath.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { mutation } from "../api/graphql.js";
	import bgContactMobile from "../assets/photos/contact-image.png?webp&w=768&imagetools";
	import bgContact from "../assets/photos/contact-image.png?webp&w=1920&imagetools";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { getTranslate } from "../utils/getTranslate.js";
	import translation from "./ContactForm.translations.json" with { type: "json" };
	import Button from "./common/Button.svelte";
	import Formatted from "./common/Formatted.svelte";
	import Link from "./common/Link.svelte";
	import Media from "./common/Media.svelte";
	import Modal from "./common/Modal.svelte";
	import TextWithIcon from "./common/TextWithIcon.svelte";
	import { ButtonVariant } from "./common/getButtonClass.js";
	import Form from "./form/Form.svelte";
	import LabelledInput from "./form/LabelledInput.svelte";
	import LabelledTextarea from "./form/LabelledTextarea.svelte";
	import PhoneInput from "./form/PhoneInput.svelte";

	const {
		heading,
		service,
		productName,
	}: Omit<HTMLFormAttributes, "onsubmit"> & {
		heading: "default" | "withText" | "getPrice" | "inquiry";
		service?: string;
		productName?: string;
	} = $props();

	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();
	const { tenant } = getGlobalTemplateProps();
	const translate = getTranslate(translation);
	const isInquiry = heading === "inquiry";
	const formName = heading === "inquiry" ? "demand" : heading === "getPrice" ? "component" : "contact";

	let showSuccessModal = $state<boolean>(false);
	let gdprApprovedAt = $state<string | null>(null);
	let phoneValue = $state<string | null>(null);
	let phoneInput: HTMLInputElement | null = $state(null);

	function onGdprToggle(event: Event): void {
		const input = event.target as HTMLInputElement;
		gdprApprovedAt = input.checked ? new Date().toISOString() : null;
	}

	function formatPhoneForDataLayer(phone: string): string {
		return phone.replace(/[\s\-()]/gu, "");
	}

	async function sha256(value: string): Promise<string> {
		const encoder = new TextEncoder();
		const data = encoder.encode(value.trim().toLowerCase());
		const hashBuffer = await crypto.subtle.digest("SHA-256", data);
		return Array.from(new Uint8Array(hashBuffer))
			.map((b) => b.toString(16).padStart(2, "0"))
			.join("");
	}

	async function trackFormSubmit(
		email: string,
		phoneNumber: string,
		message: string,
		name: string,
		formName: string,
		formId: string,
		company?: string,
		requestedService?: string,
	): Promise<void> {
		const formattedPhone = formatPhoneForDataLayer(phoneNumber);
		const [hashedEmail, hashedPhone] = await Promise.all([sha256(email), sha256(formattedPhone)]);

		if (typeof window !== "undefined") {
			if (typeof window.dataLayer === "undefined") {
				window.dataLayer = [];
			}

			window.dataLayer.push({
				event: "form_submit",
				form_name: formName,
				form_id: formId,
				user: {
					mail: hashedEmail,
					tel: hashedPhone,
					name,
					message,
					company,
					...(requestedService && { requestedService }),
				},
			});
		}
	}

	const send = createPromiseWithStatus(async (input: WebMessageInput) =>
		mutation<string, { input: typeof input }>(PUBLIC_GRAPHQL_ENDPOINT, sendWebMessage, { input }),
	);

	async function submit(event: SubmitEvent): Promise<void> {
		event.preventDefault();
		if (!gdprApprovedAt) {
			throw new Error("GDPR must be approved.");
		}
		const form = event.target as HTMLFormElement;
		const formData = new FormData(form);
		const email = formData.get("email") as string;
		const name = formData.get("fullName") as string;
		const phoneNumberRaw = formData.get("phoneNumber") as string;
		// Clean phone number - remove spaces and other formatting characters
		const phoneNumber = phoneNumberRaw.replace(/\s+/gu, "");
		const message = formData.get("message") as string;

		// Pro inquiry formulář získáme i volitelné hodnoty
		const company = isInquiry ? (formData.get("company") as string) : undefined;
		const requestedService = isInquiry ? (formData.get("requestedService") as string) : (productName ?? undefined);

		if (!email || !name || !phoneNumber || !message) {
			throw new Error("All fields are required.");
		}

		try {
			const input: WebMessageInput = {
				email,
				name,
				phoneNumber,
				message,
				gdprApprovedAt,
				locale: tenant.locale,
				...(company && { company }),
				...(requestedService && { requestedService }),
			};

			const formId = await send.call(input);
			await trackFormSubmit(email, phoneNumber, message, name, formName, formId, company, requestedService);
			showSuccessModal = true;
			form.reset();
			phoneValue = "";
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
		}
	}
</script>

<section class="full-width relative flex grid-container">
	<Media
		height={0}
		width={1920}
		alt=""
		source={{ src: bgContact, type: "image/webp" }}
		sourcesets={{ "<md": bgContactMobile }}
		class="absolute inset-0 w-full h-full object-cover"
	/>
	<div
		id={isInquiry ? inquiryId[tenant.locale] : undefined}
		class="container-center flex items-center z-10 flex-col gap-8 sm:gap-10 md:gap-12 lg:gap-16 -scroll-mt-32 md:-scroll-mt-24"
	>
		<div class="flex justify-start md:justify-center items-start md:items-center flex-col">
			<h2 class="text-white text-left md:text-center" id={contactId[tenant.locale]}>
				{#if heading === "default"}
					<Formatted text={translate("contactUs1")} />
				{:else if heading === "withText"}
					<Formatted text={translate("contactUs2")} />
				{:else if heading === "getPrice"}
					<Formatted text={translate("contactUs3")} />
				{:else if isInquiry}
					<Formatted text={translate("contactUs4")} />
				{/if}
			</h2>
			{#if heading === "withText"}
				<span
					class="text-sm sm:text-base md:text-lg/[1.4] font-light flex mt-3 sm:mt-4 md:mt-6 lg:mt-8 w-full sm:w-4/5 md:w-2/3 text-white text-left md:text-center"
					><Formatted text={translate("contactUs2text")} /></span
				>
			{:else if isInquiry}
				<span
					class="text-sm sm:text-base md:text-lg/[1.4] font-light flex mt-3 sm:mt-4 md:mt-6 lg:mt-8 w-full sm:w-4/5 md:w-2/3 text-white text-left md:text-center"
					><Formatted text={translate("contactUs4text")} /></span
				>
			{/if}
		</div>

		<Modal show={showSuccessModal}>
			<h2 class="text-xl/[1.1]"><Formatted text={translate("thankYou")} /></h2>
			<div class="items-left flex flex-col gap-8">
				<p>
					{translate("messageSent")}
				</p>

				<Button
					variant={ButtonVariant.TransparentWithIcon}
					class="ml-auto"
					onclick={(): void => {
						showSuccessModal = false;
					}}
				>
					{translate("acknowledge")}
				</Button>
			</div>
		</Modal>

		<Form
			class="grid bg-white px-4 py-6 sm:px-8 sm:py-10 md:p-12 lg:p-16 w-full rounded-lg gap-6 sm:gap-8 md:gap-8 grid-cols-1 md:grid-cols-2"
			onsubmit={submit}
		>
			<div class="md:col-span-2">
				<LabelledInput name="fullName" required><Formatted text={translate("name")} /></LabelledInput>
			</div>
			{#if isInquiry}
				<div class="md:col-span-2">
					<LabelledInput name="company"><Formatted text={translate("company")} /></LabelledInput>
				</div>
			{/if}
			<LabelledInput name="email" type="email" required><Formatted text={translate("email")} /></LabelledInput>
			<div class="flex flex-col gap-0.5 md:gap-3">
				<label for="phoneNumber" class="text-xs/[1.1] uppercase md:text-sm/[1.2]">
					<Formatted text={translate("phone")} />
				</label>
				<PhoneInput id="phoneNumber" bind:value={phoneValue} bind:input={phoneInput} required />
			</div>
			<div class="flex flex-col md:col-span-2 gap-6 sm:gap-8">
				{#if isInquiry}
					<LabelledInput name="requestedService" value={service}
						><Formatted text={translate("requestedService")} /></LabelledInput
					>
				{/if}
				<LabelledTextarea
					name="message"
					required
					class="shrink grow max-h-[12rem] sm:max-h-[14rem] h-[12rem] sm:h-[14rem]"
					><Formatted text={translate("message")} /></LabelledTextarea
				>

				<LabelledInput type="checkbox" name="gdprApprovedAt" onchange={onGdprToggle} required>
					<span class="text-xs leading-5 sm:text-base">
						{translate("agreeWith")}
						<Link href={getPrivacyPolicyPath(tenant)}><Formatted text={translate("privacyPolicy")} /></Link>
					</span>
				</LabelledInput>
			</div>

			<div class="flex justify-center md:col-span-2 mt-2 sm:mt-4">
				<Button
					type="submit"
					variant={ButtonVariant.OutlinedWithIconTight}
					class="place-self-center col-span-full min-w-[8rem] px-6 py-3"
				>
					<TextWithIcon><Formatted text={translate("send")} /></TextWithIcon>
				</Button>
			</div>
		</Form>
	</div>
</section>
